import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Clock, Eye, FileText, Loader2 } from 'lucide-react';

const AnalysisHistory = ({ analyses, onViewAnalysis, loading }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'processing': return 'bg-yellow-100 text-yellow-800';
      case 'pending': return 'bg-blue-100 text-blue-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Card className="bg-white shadow-lg h-fit">
        <CardContent className="p-6 text-center">
          <Loader2 className="w-8 h-8 text-gray-400 mx-auto mb-4 animate-spin" />
          <p className="text-gray-500">Loading history...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white shadow-lg h-fit">
      <CardHeader>
        <CardTitle className="flex items-center">
          <Clock className="w-5 h-5 mr-2 text-gray-600" />
          Analysis History
        </CardTitle>
      </CardHeader>
      <CardContent>
        {analyses.length === 0 ? (
          <div className="text-center py-6">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">
              No previous analyses found. 
              <br />
              Start your first analysis to begin building your ancestral history.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {analyses.slice(0, 5).map((analysis) => (
              <div 
                key={analysis.id} 
                className="border rounded-lg p-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 text-sm line-clamp-1">
                      {analysis.surname !== 'Multiple Family Lines' ? 
                        analysis.surname : 'Family Tree Analysis'}
                    </h4>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDate(analysis.created_at)}
                    </p>
                  </div>
                  <Badge className={`text-xs ${getStatusColor(analysis.status)}`}>
                    {analysis.status}
                  </Badge>
                </div>

                {analysis.region && (
                  <p className="text-xs text-gray-600 mb-2">
                    Region: {analysis.region}
                  </p>
                )}

                {analysis.status === 'completed' && (
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      Confidence: {((analysis.confidence_score || 0) * 100).toFixed(0)}%
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => onViewAnalysis(analysis)}
                      className="text-xs px-2 py-1 h-6"
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      View
                    </Button>
                  </div>
                )}

                {analysis.status === 'processing' && (
                  <div className="flex items-center text-xs text-yellow-600">
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Processing...
                  </div>
                )}

                {analysis.status === 'failed' && (
                  <div className="text-xs text-red-600">
                    Analysis failed - please try again
                  </div>
                )}
              </div>
            ))}

            {analyses.length > 5 && (
              <div className="text-center pt-2">
                <Button variant="ghost" size="sm" className="text-xs">
                  View All ({analyses.length} total)
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AnalysisHistory;